import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  collection,
  getDocs,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import { usePriceContext } from '../context/PriceContext';
import { BRANCH_LABELS } from '../utils/gunsonuWhatsappReport';
import { filterIslemlerByDay } from '../utils/gunsonuPdfInsight';
import { buildGunsonuPdf } from '../utils/gunsonuWhatsappPdf';
import { loadFinanceExpenses } from '../utils/finance/financeExpenseFirestore';
import { summarizeFinancePeriod } from '../utils/finance/financePnl';
import { loadFinanceCostSettings } from '../utils/finance/financeCostFirestore';
import { istanbulDateKey, istanbulStartUtc, istanbulEndUtc } from '../utils/finance/financePeriod';
import { fetchMevcutKasa } from '../utils/mevcutKasaService';
import { fetchMagazaDurumuPdfRows } from '../utils/magazaDurumuPdfSummary';
import { fetchFirmaBakiyeleri } from '../utils/firmaBakiyeleriService';
import { loadGunsonuWhatsappSettings } from '../utils/gunsonuWhatsappSettings';
import { gunsonuBranchCompareUrl, gunsonuWhatsappSendUrl } from '../utils/apiBase';

/** Son N gün işlem — günün işlemleri + haftalık kıyas için. */
async function fetchRecentIslemler(sube, days = 14) {
  const start = new Date();
  start.setDate(start.getDate() - days);
  start.setHours(0, 0, 0, 0);
  const snap = await getDocs(
    query(
      collection(db, 'subeler', sube, 'islemler'),
      where('timestamp', '>=', Timestamp.fromDate(start)),
      orderBy('timestamp', 'desc')
    )
  );
  return snap.docs.map((d) => {
    const data = d.data();
    const tarih = data.timestamp?.toDate?.() || null;
    return { id: d.id, ...data, tarih };
  });
}

/** Gecici ag/Firestore hatasinda bir kez daha dene (finans ozeti sessizce
 *  dusmesin diye: 28.09 aksami Kavakli'da tam olarak bu oldu). */
async function withRetry(fn, label) {
  try {
    return await fn();
  } catch (err) {
    await new Promise((r) => setTimeout(r, 1500));
    try {
      return await fn();
    } catch (err2) {
      throw new Error(`${label}: ${err2?.message || err2}`);
    }
  }
}

/** Ay basindan bugune kadarki islemler — finans ozeti PDF'i icin. */
async function fetchAyBasindanBugune(sube) {
  const todayKey = istanbulDateKey();
  const startKey = `${todayKey.slice(0, 7)}-01`;
  const range = {
    startYmd: startKey,
    endYmd: todayKey,
    start: istanbulStartUtc(startKey),
    end: istanbulEndUtc(todayKey)
  };
  const snap = await getDocs(
    query(
      collection(db, 'subeler', sube, 'islemler'),
      where('timestamp', '>=', range.start),
      where('timestamp', '<=', range.end),
      orderBy('timestamp', 'desc')
    )
  );
  return { range, islemler: snap.docs.map((d) => ({ id: d.id, ...d.data() })) };
}

async function fetchBranchCompare() {
  const token = await auth.currentUser?.getIdToken?.(true);
  if (!token) return [];
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 20000);
  try {
    const res = await fetch(gunsonuBranchCompareUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({}),
      signal: controller.signal,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !Array.isArray(data?.branches)) return [];
    return data.branches;
  } catch (err) {
    console.warn('[gunsonu compare]', err?.message || err);
    return [];
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Günsonu / anlık buton: Mevcut Kasa bazlı PDF → WhatsApp (sadece PDF).
 */
export default function GunsonuWhatsappSendDialog({
  open,
  onClose,
  sube,
  instantSend = false,
}) {
  const { prices } = usePriceContext() || { prices: [] };
  const pricesRef = useRef(prices);
  pricesRef.current = prices;

  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [error, setError] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [recipients, setRecipients] = useState([]);
  const [dateLabel, setDateLabel] = useState('');
  const [autoResult, setAutoResult] = useState(null);

  useEffect(() => {
    if (!open || !sube) return undefined;

    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setSending(false);
      setError('');
      setAutoResult(null);
      setStatusText('Veriler yükleniyor…');

      try {
        setStatusText('Ayarlar, kasa, firma ve şube karşılaştırması alınıyor…');
        const [settings, recentIslemler, mevcut, firmaPayload, branchCompare] = await Promise.all([
          loadGunsonuWhatsappSettings(sube),
          fetchRecentIslemler(sube, 14),
          fetchMevcutKasa(sube),
          fetchFirmaBakiyeleri(sube),
          fetchBranchCompare(),
        ]);
        if (cancelled) return;

        setEnabled(settings.enabled !== false);
        setRecipients(settings.recipients || []);

        setStatusText('Mağaza durumu hesaplanıyor…');
        let magazaRows = [];
        try {
          magazaRows = await fetchMagazaDurumuPdfRows(sube, pricesRef.current || []);
        } catch (magazaErr) {
          console.warn('[gunsonu magaza]', magazaErr);
          magazaRows = [];
        }
        if (cancelled) return;

        const now = new Date();
        const lastWeek = new Date(now);
        lastWeek.setDate(lastWeek.getDate() - 7);
        const todayIslemler = filterIslemlerByDay(recentIslemler, now);
        const lastWeekIslemler = filterIslemlerByDay(recentIslemler, lastWeek);

        const label = now.toLocaleDateString('tr-TR', {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
          weekday: 'long',
        });
        setDateLabel(label);

        // Finans ozeti (ay basindan bugune) AYRI dosya degil, ayni PDF'in son
        // sayfasi. Veri hazirlanamazsa gun sonu raporu yine de gitsin ama
        // sebebi ekranda gorunsun (Kavakli'da sessizce atlaniyordu).
        setStatusText('Finans özeti hazırlanıyor…');
        let financeSection = null;
        let financeWarn = '';
        try {
          const [{ range: finRange, islemler: finIslemler }, finGiderler, finSettings] = await Promise.all([
            withRetry(() => fetchAyBasindanBugune(sube), 'Ay içi işlemler okunamadı'),
            withRetry(() => loadFinanceExpenses(sube), 'Gider kayıtları okunamadı'),
            loadFinanceCostSettings(sube).catch(() => null)
          ]);
          const finPnl = summarizeFinancePeriod({
            islemler: finIslemler,
            giderler: finGiderler,
            prices: pricesRef.current || [],
            range: finRange,
            settings: finSettings
          });
          financeSection = {
            branchLabel: BRANCH_LABELS[sube] || sube,
            range: finRange,
            pnl: finPnl,
            giderler: finGiderler,
            prices: pricesRef.current || [],
            dateLabel: label
          };
        } catch (finErr) {
          console.warn('[finans ozeti]', finErr);
          financeWarn = finErr?.message || 'Finans özeti verisi hazırlanamadı';
        }
        if (cancelled) return;

        setStatusText('PDF oluşturuluyor…');
        let pdf = null;
        try {
          pdf = await buildGunsonuPdf({
            subeKey: sube,
            dateLabel: label,
            kasaData: mevcut.kasaData,
            allIslemler: recentIslemler,
            todayIslemler,
            lastWeekIslemler,
            magazaRows,
            firmaRows: firmaPayload.rows,
            firmaGenelToplam: firmaPayload.genelToplam,
            branchCompare,
            finance: financeSection,
          });
          if (pdf?.financeError) financeWarn = pdf.financeError;
        } catch (pdfErr) {
          console.warn('[gunsonu pdf]', pdfErr);
          if (!cancelled) {
            setError(pdfErr?.message || 'PDF oluşturulamadı.');
            setAutoResult({ mode: 'failed' });
          }
          return;
        }
        if (cancelled) return;

        if (settings.enabled === false) {
          setAutoResult({
            mode: 'skipped',
            message: 'WhatsApp raporu bu şube için kapalı.',
          });
          return;
        }
        if (!settings.recipients?.length) {
          setAutoResult({
            mode: 'skipped',
            message: 'Alıcı yok. Yönetici → WhatsApp Ayarları.',
          });
          return;
        }
        if (!pdf?.base64) {
          setError('PDF üretilemedi.');
          return;
        }

        setLoading(false);
        setSending(true);
        setStatusText('PDF WhatsApp’a gönderiliyor…');

        const token = await auth.currentUser?.getIdToken?.(true);
        if (!token) {
          if (!cancelled) setError('Oturum bulunamadı. Tekrar giriş yapıp deneyin.');
          return;
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 90000);
        let res;
        let data = {};
        try {
          res = await fetch(gunsonuWhatsappSendUrl(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              sube,
              dateLabel: label,
              branchLabel: BRANCH_LABELS[sube] || sube,
              pdfBase64: pdf.base64,
              pdfFilename: pdf.filename,
            }),
            signal: controller.signal,
          });
          data = await res.json().catch(() => ({}));
        } catch (fetchErr) {
          if (fetchErr?.name === 'AbortError') {
            throw new Error('WhatsApp gönderimi zaman aşımına uğradı (90 sn).');
          }
          throw fetchErr;
        } finally {
          clearTimeout(timeoutId);
        }
        if (cancelled) return;

        if (res.status === 503 && data?.code === 'WHATSAPP_NOT_CONFIGURED') {
          setAutoResult({
            mode: 'manual',
            message: 'Cloud API henüz hazır değil. PDF hazır; token/ayar gerekir.',
          });
          return;
        }

        if (!res.ok && res.status !== 207) {
          setError(data?.error || `Gönderim hatası (${res.status})`);
          setAutoResult({ mode: 'failed', results: data?.results || [] });
          return;
        }

        if (data?.skipped) {
          setAutoResult({
            mode: 'skipped',
            reason: data.reason,
            message:
              data.reason === 'no_recipients'
                ? 'Alıcı yok.'
                : 'WhatsApp raporu bu şube için kapalı.',
          });
          return;
        }

        const results = Array.isArray(data?.results) ? data.results : [];
        const okCount = results.filter((r) => r.ok).length;
        // Finans sayfasi eklenemediyse sessiz kalma; rapor gitti ama eksik.
        const finNote = financeWarn ? ` (Finans özeti sayfası eklenemedi: ${financeWarn})` : '';
        setAutoResult({
          mode: data?.success ? (financeWarn ? 'partial' : 'ok') : 'partial',
          results,
          message: data?.success
            ? `PDF rapor ${okCount} alıcıya WhatsApp’tan gönderildi.${finNote}`
            : `PDF gönderilemedi: ${okCount}/${results.length} alıcı başarılı.${finNote}`,
        });
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Rapor hazırlanamadı / gönderilemedi.');
          setAutoResult({ mode: 'failed' });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSending(false);
          setStatusText('');
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // prices bilerek bağımlılık değil — canlı fiyat her tick'te yeniden başlatıyordu.
  }, [open, sube, instantSend]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '12px',
          border: '1px solid #dce5f1',
          background: 'linear-gradient(180deg, #ffffff 0%, #f6f9ff 100%)',
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 800, color: '#102a43', pb: 1 }}>
        {instantSend ? 'Anlık PDF WhatsApp gönderimi' : 'Günsonu kaydedildi'}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, color: '#334155', mb: 2, fontWeight: 600 }}>
          Mevcut Kasa baz alınarak PDF hazırlanır ve WhatsApp’tan (sadece PDF) gönderilir.
        </Typography>

        {loading || sending ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 3 }}>
            <CircularProgress size={28} />
            <Typography sx={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
              {statusText || (sending ? 'PDF WhatsApp’a gönderiliyor…' : 'PDF rapor hazırlanıyor…')}
            </Typography>
          </Box>
        ) : (
          <>
            {error ? (
              <Typography sx={{ color: '#dc2626', fontWeight: 700, fontSize: 13, mb: 1.5 }}>
                {error}
              </Typography>
            ) : null}

            {autoResult?.message ? (
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  mb: 1.5,
                  color:
                    autoResult.mode === 'ok'
                      ? '#065f46'
                      : autoResult.mode === 'partial' || autoResult.mode === 'manual'
                        ? '#b45309'
                        : '#334155',
                }}
              >
                {autoResult.message}
              </Typography>
            ) : null}

            {Array.isArray(autoResult?.results) && autoResult.results.length ? (
              <Box sx={{ mb: 1.5 }}>
                {autoResult.results.map((r) => (
                  <Typography
                    key={r.id || r.phone}
                    sx={{
                      fontSize: 12,
                      color: r.ok ? '#065f46' : '#dc2626',
                      fontWeight: 600,
                    }}
                  >
                    {r.ok ? '✓' : '✗'} {r.name} ({r.phone}) · PDF
                    {!r.ok && r.parts?.[0]?.error ? ` — ${r.parts[0].error}` : ''}
                  </Typography>
                ))}
              </Box>
            ) : null}

            {!enabled ? (
              <Typography sx={{ fontSize: 13, color: '#b45309', fontWeight: 700, mb: 1.5 }}>
                Bu şube için günsonu WhatsApp raporu yönetici panelinden kapalı.
              </Typography>
            ) : null}

            {!enabled ? null : recipients.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: '#64748b', mb: 1 }}>
                Bu şube için alıcı tanımlı değil. Yönetici → WhatsApp Ayarları.
              </Typography>
            ) : null}

            {dateLabel ? (
              <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 1 }}>{dateLabel}</Typography>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>
          Kapat
        </Button>
      </DialogActions>
    </Dialog>
  );
}
